import { errorView, queryView, type ErrorView, type QueryResultLike } from '@krp/client-runtime'

import type { OwnCashier, OwnCashierRoster } from '../api/contracts'
import { CASHIERS_WRITE_DISABLED, type CashierArchiveVm, type CashierDraft, type CashierListRow, type OwnCashiersVm } from './types'

/** Предел длины имени — лимит Cashiers (200); служба проверит то же самое. */
export const CASHIER_NAME_MAX = 200

/** Состояние команды записи (mutation) для экрана. */
export interface CashierCommandState {
  isLoading: boolean
  isError: boolean
  error?: unknown
}

/** Имя без управляющих символов: так требует служба. */
const hasControl = (value: string) => [...value].some((char) => char.charCodeAt(0) < 32 || char.charCodeAt(0) === 127)

const sameName = (a: string, b: string) => a.trim().toLocaleLowerCase('ru-RU') === b.trim().toLocaleLowerCase('ru-RU')

/**
 * Что мешает записать имя. Дубликат ищется среди действующих (как на сервере): имя архивного кассира можно занять заново.
 * Это подсказка до отправки — окончательно решает служба (`cashier-name-duplicate`).
 */
export function cashierNameProblem(name: string, items: readonly OwnCashier[], selfId: string | null): string | null {
  const trimmed = name.trim()
  if (trimmed === '') return 'Укажите имя кассира'
  if (trimmed.length > CASHIER_NAME_MAX) return `Имя кассира — не длиннее ${CASHIER_NAME_MAX} символов`
  if (hasControl(trimmed)) return 'В имени не должно быть управляющих символов'
  const clash = items.find((item) => !item.archived && item.cashierId !== selfId && sameName(item.name, trimmed))
  return clash === undefined ? null : `Кассир «${clash.name}» уже есть среди действующих — имена не должны совпадать`
}

export function commandErrorOf(state: CashierCommandState): ErrorView | null {
  return state.isError && state.error !== undefined ? errorView(state.error) : null
}

export function cashierRow(item: OwnCashier): CashierListRow {
  return {
    id: item.cashierId,
    name: item.name,
    archived: item.archived,
    statusText: item.archived ? 'В архиве' : 'Действует',
    revisionText: `версия ${item.revision}`,
  }
}

export interface CashiersVmInput {
  roster: QueryResultLike<OwnCashierRoster>
  draft: CashierDraft | null
  command: CashierCommandState
  archive: { cashierId: string; working: boolean; stage: string | null; error: { title: string; view: ErrorView } | null } | null
  /** Есть ли право отзывать доступ в Cashiers; без него архивирование идёт, но предупреждение стоит до подтверждения. */
  canRevoke: boolean
  revokeWarning: string
  rowError: { title: string; view: ErrorView } | null
  done: string | null
  warning: string | null
  canWrite: boolean
}

export function cashiersVm(input: CashiersVmInput): OwnCashiersVm {
  const items = input.roster.data?.items ?? []
  const draft = input.draft
  const archiveTarget = input.archive === null ? undefined : items.find((item) => item.cashierId === input.archive?.cashierId)
  const archive: CashierArchiveVm | null =
    input.archive === null
      ? null
      : {
          cashierId: input.archive.cashierId,
          name: archiveTarget?.name ?? 'кассир',
          working: input.archive.working,
          stage: input.archive.stage,
          revokeWarning: input.canRevoke ? null : input.revokeWarning,
          error: input.archive.error,
        }
  return {
    query: queryView(input.roster, (roster) => roster.items.length === 0),
    rows: items.map(cashierRow),
    form:
      draft === null
        ? null
        : {
            draft,
            problem: cashierNameProblem(draft.name, items, draft.id),
            saving: input.command.isLoading,
            error: commandErrorOf(input.command),
          },
    archive,
    rowError: input.rowError,
    done: input.done,
    warning: input.warning,
    writeDisabledReason: input.canWrite ? null : CASHIERS_WRITE_DISABLED,
  }
}
